/* ============================================================
   Sport Zone store tour hero — assets/hero-store-tour.js
   Paired with snippets/hero-store-tour.liquid + hero-store-tour.css

   Crossfades a handful of photographs of the shop behind a fixed
   overlay. Deliberately small: no dependencies, no theme coupling.

   Accessibility notes, because they drove the design:

   - WCAG 2.2.2 (Pause, Stop, Hide) requires a way to stop moving
     content that runs longer than five seconds. Rather than add a
     play/pause button the merchant did not want, the first manual
     interaction with a dot stops autoplay for good. Choosing a slide
     is a clear statement that the visitor wants control, and it is a
     documented pause mechanism that costs no extra chrome.
   - Autoplay never starts under prefers-reduced-motion.
   - Inactive slides are marked inert, so their links leave the tab
     order instead of becoming invisible keyboard traps.
   ============================================================ */

if (!customElements.get('store-tour-hero')) {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class StoreTourHero extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      this.root = this.querySelector('.szt');
      this.slides = Array.from(this.querySelectorAll('.szt__slide'));
      this.dots = Array.from(this.querySelectorAll('.szt__dot'));

      if (!this.root || this.slides.length < 2) {
        this.#warmRemainingImages();
        return;
      }

      this.ready = true;
      this.abortController = new AbortController();

      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.interval = Number(this.dataset.interval) || 5000;
      this.fade = Number(this.dataset.fade) || 900;
      this.autoplayWanted = this.dataset.autoplay === 'true';
      this.userTookOver = false;
      this.visible = true;
      this.focusHeld = false;

      this.#bindDots();
      this.#bindPauseTriggers();
      this.#observeViewport();
      this.#warmRemainingImages();

      this.play();
    }

    disconnectedCallback() {
      this.pause();
      this.abortController?.abort();
      this.intersectionObserver?.disconnect();
      this.ready = false;
    }

    /* ---------------- Timing ---------------- */

    get canAutoplay() {
      return (
        this.autoplayWanted &&
        !this.userTookOver &&
        !this.focusHeld &&
        this.visible &&
        !document.hidden &&
        !REDUCED_MOTION.matches
      );
    }

    play() {
      this.pause();
      if (!this.canAutoplay) return;
      this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
    }

    pause() {
      clearInterval(this.timer);
      this.timer = null;
    }

    /* ---------------- Transition ---------------- */

    goTo(rawIndex) {
      const total = this.slides.length;
      const next = ((rawIndex % total) + total) % total;
      if (next === this.index) return;

      const outgoing = this.slides[this.index];
      const incoming = this.slides[next];

      // The outgoing frame is held at full opacity underneath while the
      // incoming one fades in over it. Without this the backdrop colour
      // shows through the middle of every dissolve.
      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-prev');
      outgoing.setAttribute('inert', '');
      outgoing.setAttribute('aria-hidden', 'true');

      incoming.classList.remove('is-prev');
      incoming.classList.add('is-active');
      incoming.removeAttribute('inert');
      incoming.setAttribute('aria-hidden', 'false');

      clearTimeout(this.prevTimer);
      this.prevTimer = setTimeout(() => outgoing.classList.remove('is-prev'), this.fade);

      this.index = next;
      this.#syncDots();
    }

    /* ---------------- Controls ---------------- */

    #bindDots() {
      const { signal } = this.abortController;

      this.dots.forEach((dot, i) => {
        dot.addEventListener(
          'click',
          () => {
            // Picking a slide is the visitor taking the wheel. Autoplay
            // stops permanently: this is the WCAG 2.2.2 pause mechanism.
            this.userTookOver = true;
            this.pause();
            this.goTo(i);
          },
          { signal }
        );
      });

      // Left/right arrows move between dots, matching the native tablist
      // behaviour keyboard users already expect.
      this.addEventListener(
        'keydown',
        (event) => {
          const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
          if (!step) return;

          const from = this.dots.indexOf(document.activeElement);
          if (from === -1) return;

          event.preventDefault();
          const to = (from + step + this.dots.length) % this.dots.length;
          this.userTookOver = true;
          this.pause();
          this.goTo(to);
          this.dots[to].focus();
        },
        { signal }
      );
    }

    #syncDots() {
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', String(i === this.index));
      });
    }

    #bindPauseTriggers() {
      const { signal } = this.abortController;

      // Hovering deliberately does NOT pause: the photography should keep
      // moving while a shopper reads the overlay or reaches for the dots.
      // Keyboard focus still holds it, because a slide changing underneath
      // someone who is tabbing through the dots moves their target.
      const suspend = () => {
        this.focusHeld = true;
        this.pause();
      };
      const resume = () => {
        this.focusHeld = false;
        this.play();
      };

      this.addEventListener('focusin', suspend, { signal });
      this.addEventListener('focusout', resume, { signal });

      document.addEventListener(
        'visibilitychange',
        () => (document.hidden ? this.pause() : this.play()),
        { signal }
      );

      // Someone can switch reduced motion on mid-visit.
      REDUCED_MOTION.addEventListener('change', () => this.play(), { signal });
    }

    // A hero scrolled past should not keep burning frames on a phone.
    #observeViewport() {
      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry.isIntersecting;
          this.visible ? this.play() : this.pause();
        },
        { threshold: 0.2 }
      );

      this.intersectionObserver.observe(this);
    }

    /* ---------------- Loading ---------------- */

    // Only the first slide is eager, to keep the LCP image uncontested.
    // The rest are promoted once the browser is idle, well before the
    // first crossfade needs them — otherwise a lazy image inside a
    // transparent slide starts loading only as it becomes visible and
    // the dissolve reveals an empty frame.
    #warmRemainingImages() {
      const warm = () => {
        this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
          img.fetchPriority = 'low';
        });
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(warm, { timeout: 2000 });
      } else {
        setTimeout(warm, 600);
      }
    }
  }

  customElements.define('store-tour-hero', StoreTourHero);
}
